import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { criar, definirCallbackExpiracao, guardarToken, limparToken, lerToken, obter } from './api';
import type { Utilizador } from './tipos';

interface ContextoAuth {
  utilizador: Utilizador | null;
  aCarregar: boolean;
  entrar: (email: string, password: string) => Promise<void>;
  sair: () => void;
}

const Contexto = createContext<ContextoAuth | null>(null);

export function ProvedorAuth({ children }: { children: ReactNode }) {
  const [utilizador, setUtilizador] = useState<Utilizador | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const sair = useCallback(() => {
    limparToken();
    setUtilizador(null);
  }, []);

  // A 401 from any request drops the session here, so an expired token is
  // handled once rather than in every page.
  useEffect(() => { definirCallbackExpiracao(sair); }, [sair]);

  useEffect(() => {
    if (!lerToken()) {
      setACarregar(false);
      return;
    }
    obter<{ utilizador: Utilizador }>('/auth/eu')
      .then(({ utilizador: u }) => setUtilizador(u))
      .catch(() => limparToken())
      .finally(() => setACarregar(false));
  }, []);

  const entrar = useCallback(async (email: string, password: string) => {
    const { token, utilizador: u } = await criar<{ token: string; utilizador: Utilizador }>(
      '/auth/login', { email, password },
    );
    guardarToken(token);
    setUtilizador(u);
  }, []);

  const valor = useMemo(
    () => ({ utilizador, aCarregar, entrar, sair }),
    [utilizador, aCarregar, entrar, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): ContextoAuth {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAuth tem de ser usado dentro de <ProvedorAuth>.');
  return contexto;
}
